import React, { useContext } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import FlexCentered from 'components/Common/FlexCentered';
import Loading from 'components/Loading';
import useFormatPaymentAmount from 'components/DriverAX/components/AdditionalPayments/useFormatPaymentAmount';
import usePaymentCreatorName from 'components/DriverAX/components/AdditionalPayments/usePaymentCreatorName';
import { getPaymentDisplay } from 'components/DriverAX/components/AdditionalPayments/utils';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import isoDateToAmerican from 'isoDateToAmerican';
import Card from 'kingpin/atoms/Card';
import Typography from 'kingpin/atoms/Typography';

// Matches the bonus reward criteria card (BonusRewardBody's BodyListDiv).
const PaymentsCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  padding: 16px 24px;
  border: 1px solid var(--color-border-tertiary);
  background-color: var(--color-bg-primary);
  box-sizing: border-box;
`;

const PaymentLine = ({
  payment,
  isDisqualified,
}: {
  payment: BonusTypes.AdditionalPayments.Payment;
  isDisqualified: boolean;
}) => {
  const formatAmount = useFormatPaymentAmount();
  const createdBy = usePaymentCreatorName(payment);
  const { title, detail } = getPaymentDisplay(payment);

  const byline = [createdBy, isoDateToAmerican(payment.createdOn)]
    .filter((part) => !!part)
    .join(' · ');

  return (
    <Row
      spaceBetween
      style={{ gap: 16, alignItems: 'flex-start' }}
      data-testid={`recognition-payment-${payment.id}`}
    >
      {/* Typography.Body is inline, so stack the lines in a column. */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography.Body type={'Label'}>{title}</Typography.Body>
        {detail && <Typography.Body type={'Body 13'}>{detail}</Typography.Body>}
        <Typography.Body type={'Body 12'} color={'var(--color-text-muted)'}>
          {byline}
        </Typography.Body>
      </div>
      <div style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        <Typography.Body
          type={'Label'}
          color={
            isDisqualified
              ? 'var(--color-text-muted)'
              : 'var(--color-status-success)'
          }
        >
          {`+${formatAmount(payment.amount)}`}
        </Typography.Body>
      </div>
    </Row>
  );
};

// The driver's recognition payments for the selected period (from the loaded
// reward record), laid out like the bonus reward sections: the heading (see
// RecognitionPaymentsHeading) carries the total, the card lists the payments.
// Read-only: they're added / managed via the Adjust menu.
const RecognitionPayments = ({
  bonusOverview,
}: {
  bonusOverview: BonusTypes.BonusOverview;
}) => {
  const { actualRewardOutput } = bonusOverview;
  const payments = actualRewardOutput.additionalPayments ?? [];
  const isDisqualified = actualRewardOutput.eligibleForBonus === 'disqualified';

  return (
    <PaymentsCard data-testid={'recognition-payments-section'}>
      {payments.length === 0 ? (
        <Typography.Body type={'Body 13'} color={'var(--color-text-muted)'}>
          {'No additional payments this period'}
        </Typography.Body>
      ) : (
        payments.map((payment) => (
          <PaymentLine
            key={payment.id}
            payment={payment}
            isDisqualified={isDisqualified}
          />
        ))
      )}
    </PaymentsCard>
  );
};

const Gate = () => {
  const { isLoading, bonusOverview } = useContext(BonusOverviewContext);

  if (isLoading) {
    return (
      <Card style={{ height: 150 }}>
        <Loading />
      </Card>
    );
  }

  if (!bonusOverview) {
    return (
      <FlexCentered style={{ height: 150 }}>
        <Typography.Header type={'H4'}>
          Bonus information not found
        </Typography.Header>
      </FlexCentered>
    );
  }

  return <RecognitionPayments bonusOverview={bonusOverview} />;
};

export default Gate;
